import {action, makeAutoObservable, observable, reaction} from "mobx";
import {ViewController} from "data/types/structure";
import {inject, injectable} from "inversify";
import {Bindings} from "data/constants/bindings";
import type {ILocalizationStore} from "data/stores/localization/localization.store";
import type {IRankingStore} from "data/stores/ranking/ranking.store";
import type {IRanking} from "data/providers/api/ranking.api.provider";
import type {ISquadsStore} from "data/stores/squads/squads.store";
import type {IPlayersStore} from "data/stores/players/players.store";
import type {IUserStore} from "data/stores/user/user.store";
import {LeaderboardType} from "data/enums";
import {SyntheticEvent} from "react";
import {createFanHubEvent, EventGUID} from "data/utils/analytics";

export interface ILeaderboardController extends ViewController {
	readonly i18n: ILocalizationStore;

	get leaderboards(): IRanking[];
	get isAuthorized(): boolean;
	get isNextPage(): boolean;
	get selectedLeaderboard(): LeaderboardType;
	get isRequestSuccess(): boolean;

	loadMore: () => void;
	changeTabs: (_event: SyntheticEvent, newValue: LeaderboardType) => void;
}

@injectable()
export class LeaderboardController implements ILeaderboardController {
	@observable protected _leaderboardDisposer?: ReturnType<typeof reaction>;
	get leaderboards() {
		return this._rankingStore.list;
	}

	get isRequestSuccess() {
		return this._rankingStore.isRequestSuccess;
	}

	get isNextPage() {
		return this._rankingStore.isNextPage;
	}

	get isAuthorized() {
		return this._userStore.isAuthorized;
	}

	get selectedLeaderboard() {
		return this._rankingStore.selectedLeaderboard;
	}

	constructor(
		@inject(Bindings.LocalizationStore) public readonly i18n: ILocalizationStore,
		@inject(Bindings.UserStore) public _userStore: IUserStore,
		@inject(Bindings.RankingStore) public _rankingStore: IRankingStore,
		@inject(Bindings.SquadsStore) public _squadsStore: ISquadsStore,
		@inject(Bindings.PlayersStore) public _playersStore: IPlayersStore
	) {
		makeAutoObservable(this);
	}

	loadMore = () => {
		void this._rankingStore.fetchMoreLeaderboards();
	};

	@action changeTabs = (_event: SyntheticEvent, newValue: LeaderboardType) => {
		this._rankingStore.setSelectedLeaderboard(newValue);
		this._rankingStore.resetFilter();
		this._rankingStore.resetPageCount();

		if (newValue === LeaderboardType.OVERALL) {
			createFanHubEvent(EventGUID.OverallLeaderboardClick);
		} else {
			createFanHubEvent(EventGUID.MyVotesClickFromLeaderboard);
		}
	};

	dispose() {
		this._leaderboardDisposer?.();
	}

	async init() {
		this._rankingStore.resetPageCount();

		await Promise.all([this._squadsStore.fetchSquads(), this._playersStore.fetchPlayers()]);

		this._leaderboardDisposer?.();
		this._leaderboardDisposer = reaction(
			() => this._rankingStore.selectedLeaderboard,
			(type) => {
				if (type === LeaderboardType.OVERALL) {
					void this._rankingStore.fetchOverallLeaderboards();
					return;
				}

				if (type === LeaderboardType.FAN) {
					void this._rankingStore.fetchFanLeaderboards();
					return;
				}

				if (this.isAuthorized) void this._rankingStore.fetchPersonalLeaderboards();
			},
			{fireImmediately: true}
		);
	}
}
